import { useState } from 'react';
import { Sparkles, ArrowLeft } from 'lucide-react';
import { Input } from '@components/ui/input';
import { PUBS, quote, money, type AdSize, type Months, type PubChoice } from '@generated/components/pricing';

interface Props { sizes: AdSize[]; onApply: (pub: PubChoice, sizeId: string, months: Months, prepay: boolean, summary: string) => void }

const BUSINESS = ['Restaurant / Bar / Café', 'Retail Shop', 'Real Estate', 'Health & Wellness', 'Professional Services', 'Neighborhood Services', 'Arts, Events & Nonprofit', 'Home Services & Trades', 'Product Advertising', 'Book Ad', 'Other'];
const AREAS = ['Brooklyn locals', 'Manhattan locals', 'Tourists and other visitors'] as const;
// Brooklyn only → Red Hook; Manhattan only → Village; anything wider (incl. visitors) → both papers.
function pubFor(sel: number[]): PubChoice | null {
  if (sel.length === 0) return null;
  if (sel.length === 1 && sel[0] === 0) return 'red-hook';
  if (sel.length === 1 && sel[0] === 1) return 'village';
  return 'bundle';
}
const AUDIENCE = ['Families with children', 'Young professionals', 'Seniors & long-time residents', 'Homeowners', 'Shoppers & diners', 'Business owners'];
const GOALS: { label: string; weight: number }[] = [
  { label: 'Build brand awareness — be seen', weight: 2 },
  { label: 'Promote a sale, event or opening', weight: 1 },
  { label: 'Stay top-of-mind steadily', weight: 0 },
];
const TIERS = [{ label: '$0 – $100', max: 100 }, { label: '$101 – $250', max: 250 }, { label: '$251 – $500', max: 500 }, { label: 'More than $500', max: 2000 }];
const QUESTIONS = ['Describe your business.', 'Who are your customers?', 'Who are you trying to reach?', 'What is your main goal?', 'What can you comfortably spend each month on local advertising?'];

function Chip({ on, children, onClick }: { on: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`min-h-11 border px-4 py-2 text-left text-sm ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background hover:border-foreground/50'}`}>{children}</button>
  );
}

export function BudgetPlanner({ sizes, onApply }: Props) {
  const [q, setQ] = useState(0);
  const [biz, setBiz] = useState('');
  const [bizDesc, setBizDesc] = useState('');
  const [areaSel, setAreaSel] = useState<number[]>([]);
  const area = pubFor(areaSel);
  const toggleArea = (i: number) => setAreaSel((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i].sort()));
  const [aud, setAud] = useState<string[]>([]);
  const [goal, setGoal] = useState<number | null>(null);
  const [term, setTerm] = useState<Months | null>(null);
  const months: Months = term ?? 3;
  const [tier, setTier] = useState<number | null>(null);
  const [show, setShow] = useState(false);
  const amount = tier !== null ? (TIERS[tier]?.max ?? 0) : 0;
  const answered = [!!biz || !!bizDesc.trim(), !!area, aud.length > 0, goal !== null, amount > 0 && term !== null][q];

  const pubOrder: PubChoice[] = area === 'bundle' ? ['bundle', 'red-hook', 'village'] : area ? [area] : ['red-hook'];
  const w = GOALS[goal ?? 0]?.weight ?? 0;
  const options = sizes.flatMap((s) => pubOrder.flatMap((p) => [false, true].map((prepay) => ({ s, p, prepay, qt: quote(s, p, months, prepay) }))))
    .filter((o) => o.qt.total / months <= amount);
  // Awareness → biggest ad; steady presence → leaves budget room; promo → in between
  const score = (o: (typeof options)[number]) => (o.p === area ? 1e6 : 0) + (o.s.pricePerIssue ?? 0) * (w + 1) - (o.prepay ? 1 : 0) - (w === 0 ? o.qt.total / 4 : 0);
  const seen = new Set<string>();
  const picks = [...options].sort((a, b) => score(b) - score(a))
    .filter((o) => { const k = `${o.s.id}-${o.p}`; if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, 3);
  const cheapest = sizes.length ? Math.min(...sizes.map((s) => quote(s, area ?? 'red-hook', months, true).total)) : 0;
  const summary = `Campaign planner — Business: ${biz}${bizDesc.trim() ? ` — ${bizDesc.trim()}` : ''}; Customers: ${areaSel.map((i) => AREAS[i]).join(', ')}; Audience: ${aud.join(', ')}; Goal: ${GOALS[goal ?? 0]?.label ?? ''}; Budget: ${tier !== null ? TIERS[tier]?.label : ''} per month, ${months} months`;

  const next = () => { if (q < 4) setQ(q + 1); else setShow(true); };

  return (
    <section className="border-y-4 border-double border-foreground bg-card p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary"><Sparkles className="h-4 w-4" />Campaign Planner</div>
        <div className="text-xs text-muted-foreground">{show ? 'Your recommendations' : `Question ${q + 1} of 5`}</div>
      </div>
      <div className="mt-2 h-1 bg-muted"><div className="h-1 bg-primary transition-all" style={{ width: `${show ? 100 : (q / 5) * 100}%` }} /></div>

      {!show && (
        <>
          {q === 0 && (
            <div className="mt-5 border-2 border-foreground bg-[hsl(var(--brand-yellow))] p-4 text-foreground shadow-[4px_4px_0_0_hsl(var(--foreground))]">
              <p className="font-[family-name:var(--font-heading)] text-xl font-bold leading-snug">Here's how this works:</p>
              <ol className="mt-2 space-y-1.5 text-base font-medium leading-snug">
                <li><strong>1. Your business and goals.</strong> Tell us who you are and what you want your ads to do, and we'll help you find the right message.</li>
                <li><strong>2. Your budget.</strong> Then we'll recommend the ad size and how often to run it, so your money goes as far as possible.</li>
              </ol>
            </div>
          )}
          <h2 className="mt-5 font-[family-name:var(--font-heading)] text-2xl font-semibold">{QUESTIONS[q]}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {q === 0 && BUSINESS.map((b) => <Chip key={b} on={biz === b} onClick={() => setBiz(b)}>{b}</Chip>)}
            {q === 1 && <p className="w-full text-sm text-muted-foreground">Pick all that apply. Visitors include people planning a trip here, who read us online.</p>}
            {q === 1 && AREAS.map((a, i) => <Chip key={a} on={areaSel.includes(i)} onClick={() => toggleArea(i)}>{areaSel.includes(i) ? `✓ ${a}` : a}</Chip>)}
            {q === 2 && AUDIENCE.map((a) => <Chip key={a} on={aud.includes(a)} onClick={() => setAud(aud.includes(a) ? aud.filter((x) => x !== a) : [...aud, a])}>{a}</Chip>)}
            {q === 3 && GOALS.map((g, i) => <Chip key={g.label} on={goal === i} onClick={() => setGoal(i)}>{g.label}</Chip>)}
            {q === 4 && (
              <div className="flex w-full flex-col gap-4">
                <div className="flex flex-wrap gap-2">{TIERS.map((t, i) => <Chip key={t.label} on={tier === i} onClick={() => setTier(i)}>{t.label}{t.max <= 500 ? ' per month' : ' a month'}</Chip>)}</div>
                <div className="space-y-1.5">
                  <div className="text-sm font-medium">Are you thinking short term, or throughout the year?</div>
                  <div className="flex flex-wrap gap-2"><Chip on={term === 3} onClick={() => setTerm(3)}>Short term</Chip><Chip on={term === 12} onClick={() => setTerm(12)}>Throughout the year</Chip></div>
                </div>
              </div>
            )}
          </div>
          {q === 0 && (
            <div className="mt-4 space-y-1.5">
              <label htmlFor="bizDesc" className="text-sm font-medium">Tell us a little about it (optional)</label>
              <textarea id="bizDesc" rows={3} value={bizDesc} onChange={(e) => setBizDesc(e.target.value)} placeholder="e.g. Family-run Italian restaurant on Van Brunt Street, known for brunch and live music." className="w-full border border-border bg-background p-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          )}
          {q === 2 && <p className="mt-2 text-xs text-muted-foreground">Pick all that apply.</p>}
          <div className="mt-6 flex items-center justify-between">
            <button type="button" onClick={() => setQ(q - 1)} className={`inline-flex min-h-11 items-center gap-1 text-sm font-medium ${q === 0 ? 'invisible' : ''}`}><ArrowLeft className="h-4 w-4" />Back</button>
            <button type="button" onClick={next} disabled={!answered} className="min-h-11 bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-40">{q < 4 ? 'Next question' : 'See my recommendations'}</button>
          </div>
        </>
      )}

      {show && (
        <div className="mt-5">
          <p className="text-sm text-muted-foreground">For <strong className="text-foreground">{biz ? biz.toLowerCase() : 'your business'}</strong> reaching <strong className="text-foreground">{aud.join(', ').toLowerCase()}</strong> with {tier === 3 ? "more than $500" : `up to ${money(amount)}`} per month, {months === 12 ? "throughout the year" : "short term"}:</p>
          {picks.length === 0 && <p className="mt-4 text-sm">The smallest {months}-month campaign in your area starts at {money(cheapest / months)} per month. Try a higher budget range, or call George at 917-652-9128.</p>}
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {picks.map((o, i) => (
              <div key={`${o.s.id}-${o.p}-${o.prepay}`} className={`flex flex-col border bg-background p-4 ${i === 0 ? 'border-primary ring-1 ring-primary' : 'border-border'}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{i === 0 ? 'Best match' : 'Alternative'}</div>
                <div className="mt-1 font-[family-name:var(--font-body)] text-xl font-bold text-[hsl(var(--brand-blue))]">{o.s.name}</div>
                <div className="text-sm text-muted-foreground">{PUBS.find((p) => p.id === o.p)?.name} · {months} months{o.prepay ? ' · prepaid' : ''}</div>
                <div className="mt-3 font-[family-name:var(--font-heading)] text-4xl font-bold tabular-nums">{money(o.qt.monthlyEffective)}<span className="ml-1 text-base font-normal text-muted-foreground">per month</span></div>
                <button type="button" onClick={() => onApply(o.p, o.s.id, months, o.prepay, summary)} className="mt-4 min-h-11 bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90">Choose this campaign</button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setShow(false)} className="mt-4 inline-flex min-h-11 items-center gap-1 text-sm font-medium"><ArrowLeft className="h-4 w-4" />Change my answers</button>
        </div>
      )}
    </section>
  );
}
